import type { AnalyticsApiHealthDto, AnalyticsGrain } from '@pams/shared';
import { FigureList, RankedList } from '@/components/DataFigures';
import {
  formatCount,
  latencyText,
  seriesPeak,
  seriesPointLabel,
  seriesTotals,
  type SeriesPoint,
} from '@/lib/analyticsReport';
import { USAGE_ANALYTICS_COPY } from '../copy';
import { SubBlock } from './AnalyticsSection';
import { SeriesBars } from './SeriesBars';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  HOW THE API ANSWERED. [ANALYTICS §5]
 *
 *  ⚠️ 4xx IS NOT "ERRORS", AND THE LABEL SAYS SO. An access token lives thirty minutes, so every
 *  signed-in officer produces an expired-token 401 in the ordinary course of a shift and the app
 *  refreshes and retries. A 4xx count read as a failure rate would page somebody every morning.
 *
 *  ⚠️ RESPONSE TIMES ARE ESTIMATES FROM BUCKETS, AND ONE THAT FELL PAST THE LAST BUCKET READS
 *  "> 10 s". The server interpolates inside a histogram bucket; above the last finite bound there
 *  is no upper edge to interpolate towards, and printing that bound as a number would state a
 *  measurement nobody took. `latencyText` holds the rule.
 *
 *  ⚠️ THE SERVER ORDERS BOTH ROUTE LISTS, AND THE CLIENT NEVER RE-SORTS — `RankedList`'s rule.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const HealthSection = ({ data, grain }: { data: AnalyticsApiHealthDto; grain: AnalyticsGrain }) => {
  const copy = USAGE_ANALYTICS_COPY.health;
  /* ⚠️ Server errors are the BOTTOM segment and the darker one: the rarer, more serious series is
     the one a glance should find first. */
  const points: SeriesPoint[] = data.errorSeries.map((point) => ({
    at: point.at,
    values: [point.serverErrors, point.clientErrors],
  }));
  const [server = 0, client = 0] = seriesTotals(points, 2);
  const peak = seriesPeak(points);

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-6 @min-[880px]:grid-cols-3">
        <SubBlock title={copy.statusHeading}>
          <FigureList
            rows={[
              { key: 's2xx', label: copy.s2xx, value: formatCount(data.statusClasses.s2xx) },
              { key: 's3xx', label: copy.s3xx, value: formatCount(data.statusClasses.s3xx) },
              {
                key: 's4xx',
                label: (
                  <span className="flex flex-col">
                    <span>{copy.s4xx}</span>
                    <span className="text-xs">{copy.s4xxCaption}</span>
                  </span>
                ),
                value: formatCount(data.statusClasses.s4xx),
              },
              { key: 's5xx', label: copy.s5xx, value: formatCount(data.statusClasses.s5xx) },
              { key: 'aborted', label: copy.aborted, value: formatCount(data.statusClasses.aborted) },
            ]}
          />
        </SubBlock>

        <SubBlock title={copy.codesHeading}>
          {data.topCodes.length === 0 ? (
            <p className="text-sm text-muted-foreground">{copy.noCodes}</p>
          ) : (
            <FigureList
              rows={data.topCodes.map((entry) => ({
                key: entry.code,
                label: entry.code,
                value: formatCount(entry.count),
              }))}
            />
          )}
        </SubBlock>

        <SubBlock title={copy.latencyHeading} caption={copy.latencyCaption}>
          <FigureList
            rows={[
              {
                key: 'p50',
                label: copy.p50,
                value: copy.latency(latencyText(data.latency.p50Ms, data.latency.overflow)),
              },
              {
                key: 'p95',
                label: copy.p95,
                value: copy.latency(latencyText(data.latency.p95Ms, data.latency.overflow)),
              },
            ]}
          />
        </SubBlock>
      </div>

      <SubBlock title={copy.errorsTitle}>
        <SeriesBars
          testId="health-errors"
          series={[copy.serverErrors, copy.clientErrors]}
          points={points}
          grain={grain}
          summary={copy.errorsSummary(
            client,
            server,
            peak === null ? null : { label: seriesPointLabel(peak.at, grain), total: peak.total },
            grain,
          )}
        />
      </SubBlock>

      <div className="grid gap-6 @min-[880px]:grid-cols-2">
        <SubBlock title={copy.slowestHeading} caption={copy.slowestCaption}>
          {data.slowestRoutes.length === 0 ? (
            <p className="text-sm text-muted-foreground">{copy.noSlowest}</p>
          ) : (
            <RankedList
              rows={data.slowestRoutes.map((route) => ({
                key: route.route,
                label: route.route,
                value: route.p95Ms,
                display: copy.latency(latencyText(route.p95Ms, route.overflow)),
                caption: copy.slowestRow(route.requests),
              }))}
            />
          )}
        </SubBlock>

        <SubBlock title={copy.erroredHeading}>
          {data.mostErroredRoutes.length === 0 ? (
            <p className="text-sm text-muted-foreground">{copy.noErrored}</p>
          ) : (
            <RankedList
              rows={data.mostErroredRoutes.map((route) => ({
                key: route.route,
                label: route.route,
                value: route.clientErrors + route.serverErrors,
                caption: copy.erroredRow(route.clientErrors, route.serverErrors, route.requests),
              }))}
            />
          )}
        </SubBlock>
      </div>
    </div>
  );
};
